import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Col, Form, Row, Spinner } from 'react-bootstrap';
import consultationRestDayService, {
  computeEndDateLocal,
  formatDateFr
} from '../../services/consultationRestDayService';
import ConsultationRestDaysViewModal from './ConsultationRestDaysViewModal';
import { ClinicalButton, ClinicalButtonStack } from './ui';
import './ConsultationRestDaysPanel.css';
import { todayYmd } from '../../utils/appTimezone';

const emptyForm = () => ({
  start_date: todayYmd(),
  days_count: '3',
  clinical_motif: '',
  recommendations: ''
});

export default function ConsultationRestDaysPanel({
  consultation,
  embedded = false,
  readOnly = false,
  onSuccess
}) {
  const cid = consultation?.id;
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [previewUnsaved, setPreviewUnsaved] = useState(false);

  const endDate = useMemo(
    () => computeEndDateLocal(form.start_date, parseInt(form.days_count, 10)),
    [form.start_date, form.days_count]
  );

  const loadRecord = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    setError('');
    try {
      const res = await consultationRestDayService.get(cid);
      if (res.success && res.data) {
        const r = res.data;
        setForm({
          start_date: r.start_date || emptyForm().start_date,
          days_count: String(r.days_count || 1),
          clinical_motif: r.clinical_motif || '',
          recommendations: r.recommendations || ''
        });
        setStatus(r.status);
        setDirty(false);
      } else {
        setForm(emptyForm());
        setStatus(null);
        setDirty(false);
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => {
    if (!cid) return;
    setForm(emptyForm());
    setStatus(null);
    setDirty(false);
    loadRecord();
  }, [cid, loadRecord]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setError('');
  };

  const handleSave = async () => {
    if (!cid) return;
    setBusy(true);
    setError('');
    try {
      const res = await consultationRestDayService.save(cid, {
        start_date: form.start_date,
        days_count: parseInt(form.days_count, 10),
        clinical_motif: form.clinical_motif.trim() || null,
        recommendations: form.recommendations.trim() || null
      });
      if (!res.success) throw new Error(res.message || 'Enregistrement refusé.');
      setStatus(res.data?.status || 'ISSUED');
      setDirty(false);
      onSuccess?.();
    } catch (e) {
      setError(e.message || 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!cid || !window.confirm('Annuler ce certificat de jours de repos ?')) return;
    setBusy(true);
    setError('');
    try {
      const res = await consultationRestDayService.cancel(cid);
      if (!res.success) throw new Error(res.message || 'Annulation refusée.');
      setStatus('CANCELLED');
      onSuccess?.();
    } catch (e) {
      setError(e.message || 'Annulation impossible.');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenView = () => {
    if (!form.start_date || !form.days_count) {
      setError('Renseignez la date de début et le nombre de jours.');
      return;
    }
    setPreviewUnsaved(dirty);
    setShowViewModal(true);
  };

  if (!cid) return null;

  const isCancelled = status === 'CANCELLED';
  const hasIssued = status === 'ISSUED';

  return (
    <div
      className={`rest-days-panel${embedded ? ' rest-days-panel--embedded' : ''}${
        readOnly ? ' rest-days-panel--readonly' : ''
      }`}
    >
      {loading ? (
        <div className="text-center py-2">
          <Spinner animation="border" size="sm" />
        </div>
      ) : null}

      {error ? (
        <Alert variant="danger" className="py-2 small mb-2">
          {error}
        </Alert>
      ) : null}

      {hasIssued && !dirty ? (
        <Badge bg="success" className="mb-2 fw-normal">
          Certificat émis
        </Badge>
      ) : null}
      {isCancelled ? (
        <Badge bg="secondary" className="mb-2 fw-normal">
          Certificat annulé
        </Badge>
      ) : null}
      {dirty ? (
        <span className="small text-warning d-block mb-2">Modifications non enregistrées</span>
      ) : null}

      <Row className="g-2 mb-2">
        <Col xs={12} sm={6}>
          <Form.Group>
            <Form.Label className="small fw-semibold">Date de début</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={form.start_date}
              onChange={(e) => handleChange('start_date', e.target.value)}
              disabled={busy || isCancelled}
            />
          </Form.Group>
        </Col>
        <Col xs={6} sm={3}>
          <Form.Group>
            <Form.Label className="small fw-semibold">Durée (jours)</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              min={1}
              max={90}
              value={form.days_count}
              onChange={(e) => handleChange('days_count', e.target.value)}
              disabled={busy || isCancelled}
            />
          </Form.Group>
        </Col>
        <Col xs={6} sm={3}>
          <Form.Group>
            <Form.Label className="small fw-semibold text-muted">Fin (calculée)</Form.Label>
            <div className="rest-days-panel__end-date small">
              {endDate ? formatDateFr(endDate) : '—'}
            </div>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-2">
        <Form.Label className="small fw-semibold">Motif médical</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          size="sm"
          placeholder="Diagnostic ou motif justifiant l’arrêt de travail…"
          value={form.clinical_motif}
          onChange={(e) => handleChange('clinical_motif', e.target.value)}
          disabled={busy || isCancelled}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="small fw-semibold">Recommandations (optionnel)</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          size="sm"
          placeholder="Repos strict, reprise progressive, éviter port de charges…"
          value={form.recommendations}
          onChange={(e) => handleChange('recommendations', e.target.value)}
          disabled={busy || isCancelled}
        />
      </Form.Group>

      <ClinicalButtonStack>
        <ClinicalButton
          variant="view"
          icon="eye"
          block
          disabled={busy || isCancelled || !form.start_date || !form.days_count}
          onClick={handleOpenView}
        >
          Voir &amp; imprimer le certificat
        </ClinicalButton>
        {!isCancelled && !readOnly ? (
          <ClinicalButton
            variant="save"
            block
            loading={busy}
            disabled={!dirty}
            onClick={handleSave}
          >
            Enregistrer le certificat
          </ClinicalButton>
        ) : null}
        {hasIssued && !dirty && !readOnly ? (
          <ClinicalButton variant="danger" block disabled={busy} onClick={handleCancel}>
            Annuler le certificat
          </ClinicalButton>
        ) : null}
      </ClinicalButtonStack>

      <ConsultationRestDaysViewModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        consultation={consultation}
        restDay={{
          start_date: form.start_date,
          days_count: parseInt(form.days_count, 10),
          end_date: endDate,
          clinical_motif: form.clinical_motif,
          recommendations: form.recommendations,
          status: dirty ? 'PREVIEW' : status
        }}
        previewUnsaved={previewUnsaved}
      />
    </div>
  );
}
